import Resources from "../../../data/services/resources";
import {CREATE_PERM, UPDATE_PERM} from "../../util/util-consts";
import {readStoredJson} from "./agent-local-storage";

/**
 * The agent reaches TMS data by calling POST /mcp on the user's behalf, so that
 * permission is the meaningful gate: without it every tool call would fail.
 *
 * CREATE_PERM is imported from common/util/util-consts — NOT the parent
 * src/util/util-constants, which crashes at module eval under vitest.
 */
export const AGENT_PERM_KEY = Resources.Mcp;

/**
 * Fail-open when the key is ABSENT: on any environment whose backend predates
 * the mcp permission, a strict check would hide the feature from everyone and
 * make the whole verification checklist unrunnable. When the key is PRESENT it
 * is authoritative in both directions.
 */
export function isAgentAllowed(permissions) {
    if (!permissions || typeof permissions !== 'object') {
        return true;
    }

    if (!(AGENT_PERM_KEY in permissions)) {
        return true;
    }

    return (parseInt(permissions[AGENT_PERM_KEY]) & CREATE_PERM) !== 0;
}

/**
 * Whether anyone is signed in at all.
 *
 * This is a DIFFERENT question from isAgentAllowed's fail-open, and it needs the
 * opposite answer. Fail-open exists for a signed-in user on a deployment whose
 * backend predates the mcp permission. "Nobody is signed in" was being swallowed
 * by that same branch, which is why the sheet could render on /login: the token
 * getter returned null and every request went out as `Bearer null`.
 *
 * `access_token` is the key that matters — it is what the agent would act with,
 * and logout removes the whole stored user.
 */
export function isSignedIn(user) {
    return !!user && typeof user === 'object' && !!user.access_token;
}

export function canUseAgent() {
    const user = readStoredJson('user', null);

    if (!isSignedIn(user)) {
        return false;
    }

    return isAgentAllowed(user.permissions);
}

/**
 * Gates the model-preset picker (§10 of the handoff): "keep this behind a
 * developer or admin flag" — a dispatcher has no reason to choose a raw model
 * stack, and a wrong choice makes the assistant worse at their actual job.
 *
 * Deliberately the OPPOSITE default from isAgentAllowed above: that one fails
 * OPEN when the key is absent, because an unconfigured deployment must not
 * lock out the whole feature. This one fails CLOSED, because "the permission
 * system hasn't been told about presets yet" must mean "nobody sees the
 * preset picker," not "everybody does."
 */
export function isAgentAdmin(permissions) {
    if (!permissions || typeof permissions !== 'object') {
        return false;
    }

    if (!(AGENT_PERM_KEY in permissions)) {
        return false;
    }

    return (parseInt(permissions[AGENT_PERM_KEY]) & UPDATE_PERM) !== 0;
}

export function canManageAgentPresets() {
    const user = readStoredJson('user', null);

    if (!isSignedIn(user)) {
        return false;
    }

    return isAgentAdmin(user.permissions);
}
